import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/utils/api';

const platformOptions = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'twitter', label: 'Twitter' }
];

export default function AdminSocialMediaLinks() {
  const [links, setLinks] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState({
    platform: 'facebook',
    url: '',
    enabled: true,
    order: 0
  });

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await apiClient.get('/admin/social-media-links');
      setLinks(response.data);
    } catch (error) {
      console.error('Error fetching social media links:', error);
      toast.error('Failed to load social media links');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLink) {
        await apiClient.put(`/admin/social-media-links/${editingLink.id}`, formData);
        toast.success('Social media link updated successfully!');
      } else {
        await apiClient.post('/admin/social-media-links', formData);
        toast.success('Social media link added successfully!');
      }
      fetchLinks();
      closeModal();
    } catch (error) {
      toast.error('Failed to save social media link');
    }
  };

  const handleEdit = (link) => {
    setEditingLink(link);
    setFormData({
      platform: link.platform,
      url: link.url,
      enabled: link.enabled,
      order: link.order
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this social media link?')) return;
    
    try {
      await apiClient.delete(`/admin/social-media-links/${id}`);
      toast.success('Social media link deleted successfully!');
      fetchLinks();
    } catch (error) {
      toast.error('Failed to delete social media link');
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingLink(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      platform: 'facebook',
      url: '',
      enabled: true,
      order: 0
    });
  };

  const openAddModal = () => {
    resetForm();
    setEditingLink(null);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Social Media Links</h1>
              <p className="text-gray-600 mt-1">Manage sticky floating social media icons</p>
            </div>
            <Button onClick={openAddModal} className="bg-green-600 hover:bg-green-700">
              <Plus size={18} className="mr-2" />
              Add Link
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {links.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500 mb-4">No social media links yet.</p>
            <Button onClick={openAddModal} className="bg-green-600 hover:bg-green-700">
              <Plus size={18} className="mr-2" />
              Add Your First Link
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {links.map((link) => (
              <div key={link.id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      link.enabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                      <span className="text-xl capitalize">{link.platform[0]}</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 capitalize">{link.platform}</h3>
                      <span className={`text-xs px-2 py-1 rounded ${
                        link.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {link.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-2 break-all">
                  {link.url}
                </p>
                <p className="text-gray-400 text-xs mb-4">Order: {link.order}</p>
                <div className="flex space-x-2">
                  <Button onClick={() => handleEdit(link)} size="sm" variant="outline" className="flex-1">
                    <Edit size={16} className="mr-1" />
                    Edit
                  </Button>
                  <Button onClick={() => handleDelete(link.id)} size="sm" variant="destructive">
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={closeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingLink ? 'Edit Social Media Link' : 'Add Social Media Link'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Platform</Label>
              <Select 
                value={formData.platform} 
                onValueChange={(value) => setFormData({ ...formData, platform: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent>
                  {platformOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>URL</Label>
              <Input
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://facebook.com/yourpage"
                required
              />
            </div>

            <div>
              <Label>Display Order</Label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                min="0"
              />
              <p className="text-sm text-gray-500 mt-1">Lower numbers appear first</p>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                className="rounded"
              />
              <Label>Show on website</Label>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                <Save size={18} className="mr-2" />
                {editingLink ? 'Update Link' : 'Add Link'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
